/**
 * useInsightsCache — [T246-FE-CACHE]
 *
 * Per-filter memo cache that replaces the refetch-on-tab-activate pattern
 * previously embedded in `useWidgetData`.
 *
 * Design:
 *   • Cache key is `sha1Hex(stableStringify(filters))` — equivalent to the
 *     task packet's `sha1(JSON.stringify(filters))` but with a deterministic
 *     key-order sort so `{ a: 1, b: 2 }` and `{ b: 2, a: 1 }` hash to the
 *     same value.
 *   • Each entry carries `data` + `fetchedAt`. Reads return `{ data,
 *     fetchedAt, isStale }` so consumers can decide whether to serve
 *     immediately (fresh) or show a stale ghost while a revalidate fetch
 *     is in flight (stale-while-revalidate).
 *   • Default TTL is 60 000 ms (60 s). Configurable per-hook via `ttlMs`.
 *   • SHA-1 is used **for cache-key derivation only** — never for any
 *     security purpose. The implementation is a small public-domain
 *     reference port (RFC 3174) so we avoid `crypto.subtle.digest`, which
 *     is async-only and would force a Promise-aware Map-key path that
 *     doesn't fit React's synchronous effect-dependency model.
 *     See RFC 3174 §6: https://datatracker.ietf.org/doc/html/rfc3174
 *
 * Public API (the contract for AC1):
 *   • `get(filters)`        → `InsightsCacheHit<T> | null`
 *   • `set(filters, data)`  → `filterHash` for caller-side bookkeeping
 *   • `clear()`             → drops every entry (and aborts any tracked
 *                             in-flight fetch)
 *   • `beginFetch / endFetch / cancelInFlight` — optional helpers used by
 *     `useWidgetData` to satisfy AC5 (AbortController on mid-fetch change).
 *
 * @module hooks/useInsightsCache
 * @see .omo/plans/T246-PY-insights-graph-revamp-prd.md §T246-FE-CACHE
 */

import { useCallback, useMemo, useRef } from 'react';

// ── Constants ──────────────────────────────────────────────────────────────────

/**
 * Default cache TTL: 60 seconds.
 *
 * Tuned for the transaction-insights dashboard: short enough that users see
 * fresh data after a short break, long enough that toggling tabs back and
 * forth doesn't trigger a refetch storm.
 */
export const DEFAULT_INSIGHTS_TTL_MS = 60_000;

const SHA1_DIGEST_BYTES = 20;

// ── Public types ───────────────────────────────────────────────────────────────

/**
 * Single cache entry — keeps the payload alongside the timestamp at which
 * it was stored so the TTL check and stale-while-revalidate transition are
 * a pure synchronous read.
 */
export interface InsightsState<T> {
  /** Cached payload as supplied by the producer. */
  data: T;
  /** Epoch millis when the entry was stored. */
  fetchedAt: number;
}

/**
 * Return shape of `get(filters)`. The caller branches on `isStale`:
 *
 *   • `isStale === false` — entry is within TTL; serve immediately.
 *   • `isStale === true`  — entry exists but is older than TTL; serve the
 *                           previous value as an SWR ghost while kicking
 *                           off a background revalidation.
 */
export interface InsightsCacheHit<T> {
  data: T;
  fetchedAt: number;
  isStale: boolean;
}

export interface UseInsightsCacheOptions {
  /**
   * Override the default 60 s TTL. The configured value applies to every
   * `get(filters)` call on this hook instance.
   */
  ttlMs?: number;
}

export interface UseInsightsCacheApi<T> {
  /**
   * Read the cache for `filters`. Returns `null` if the entry is missing.
   * Returns `{ data, fetchedAt, isStale: false }` if the entry is fresh
   * (age < `ttlMs`). Returns `{ data, fetchedAt, isStale: true }` if the
   * entry exists but has expired — caller should revalidate.
   */
  get(filters: unknown): InsightsCacheHit<T> | null;
  /**
   * Store `data` under the cache slot derived from `filters`. Returns the
   * `filterHash` so consumers can call `endFetch` / `cancelInFlight` with
   * the same key when races occur.
   */
  set(filters: unknown, data: T): string;
  /**
   * Drop every entry. Any AbortController previously registered via
   * `beginFetch` is cancelled with reason `'cache:cleared'` first.
   */
  clear(): void;
  /**
   * Mark a fetch as in-flight for `filters` so the cache can cancel it if
   * the user changes filters before it resolves. Returns the `filterHash`.
   * Calling `beginFetch` for the same hash twice cancels the previous
   * controller first (reason `'cache:replaced'`).
   */
  beginFetch(filters: unknown, controller: AbortController): string;
  /**
   * Remove the in-flight registration for `filters`. Idempotent — calling
   * on an unknown hash is a no-op.
   */
  endFetch(filters: unknown): void;
  /**
   * Abort the in-flight fetch registered for `filters` (if any). The
   * underlying AbortController receives `AbortSignal.reason === 'cache:filter-changed'`.
   */
  cancelInFlight(filters: unknown): void;
}

// ── SHA-1 (RFC 3174 reference implementation, public-domain port) ─────────────
//
// Inlined so the hook stays synchronous: Map keys must be available on the
// very first render, before any async work has had a chance to settle. SHA-1
// here is purely a stable cache-key derivation — see the module header.

/**
 * SHA-1 digest of a UTF-8 string, encoded as a 40-char lowercase hex
 * string. Synchronous, pure, side-effect-free.
 *
 * SECURITY: NOT a cryptographic primitive in this module. Used only to
 * derive a deterministic cache key for a JSON-serialised filter set, where
 * collisions only impact cache-hit ratios (never user data or any
 * security boundary). Do not reuse for HMAC, signatures, or any
 * integrity-sensitive purpose.
 *
 * @see https://datatracker.ietf.org/doc/html/rfc3174
 */
export function sha1Hex(message: string): string {
  // TextEncoder is available in every browser Vite targets and in Node
  // ≥ 18 (which Jest uses). Fallback path covers no-TextEncoder envs.
  let utf8: Uint8Array;
  if (typeof TextEncoder !== 'undefined') {
    utf8 = new TextEncoder().encode(message);
  } else {
    const buf: number[] = [];
    for (let i = 0; i < message.length; i += 1) {
      const cp = message.charCodeAt(i);
      if (cp < 0x80) {
        buf.push(cp);
      } else if (cp < 0x800) {
        buf.push(0xc0 | (cp >> 6), 0x80 | (cp & 0x3f));
      } else {
        buf.push(
          0xe0 | (cp >> 12),
          0x80 | ((cp >> 6) & 0x3f),
          0x80 | (cp & 0x3f),
        );
      }
    }
    utf8 = new Uint8Array(buf);
  }

  const totalBytes = utf8.length;
  const blockBytes = 64;
  // Need >= totalBytes + 1 (for 0x80) + 8 (length) bytes.
  const paddedLength =
    (Math.floor((totalBytes + 9) / blockBytes) + 1) * blockBytes;
  const padded = new Uint8Array(paddedLength);
  padded.set(utf8);
  padded[totalBytes] = 0x80;

  // Bit-length appended as 64-bit big-endian. Filter-set serialisations
  // are always small (< 2^32 bits), so the high 32 bits are zero.
  const totalBits = totalBytes * 8;
  padded[paddedLength - 8] = 0;
  padded[paddedLength - 7] = 0;
  padded[paddedLength - 6] = 0;
  padded[paddedLength - 5] = 0;
  padded[paddedLength - 4] = (totalBits >>> 24) & 0xff;
  padded[paddedLength - 3] = (totalBits >>> 16) & 0xff;
  padded[paddedLength - 2] = (totalBits >>> 8) & 0xff;
  padded[paddedLength - 1] = totalBits & 0xff;

  let h0 = 0x67452301 | 0;
  let h1 = 0xefcdab89 | 0;
  let h2 = 0x98badcfe | 0;
  let h3 = 0x10325476 | 0;
  let h4 = 0xc3d2e1f0 | 0;

  const w = new Uint32Array(80);

  for (let block = 0; block < padded.length; block += blockBytes) {
    for (let i = 0; i < 16; i += 1) {
      const off = block + i * 4;
      w[i] =
        (((padded[off] ?? 0) << 24) |
          ((padded[off + 1] ?? 0) << 16) |
          ((padded[off + 2] ?? 0) << 8) |
          (padded[off + 3] ?? 0)) >>>
        0;
    }
    for (let i = 16; i < 80; i += 1) {
      const v =
        (w[i - 3] ?? 0) ^
        (w[i - 8] ?? 0) ^
        (w[i - 14] ?? 0) ^
        (w[i - 16] ?? 0);
      w[i] = ((v << 1) | (v >>> 31)) >>> 0;
    }

    let a = h0;
    let b = h1;
    let c = h2;
    let d = h3;
    let e = h4;

    for (let i = 0; i < 80; i += 1) {
      let f: number;
      let k: number;
      if (i < 20) {
        f = ((b & c) | (~b & d)) >>> 0;
        k = 0x5a827999;
      } else if (i < 40) {
        f = (b ^ c ^ d) >>> 0;
        k = 0x6ed9eba1;
      } else if (i < 60) {
        f = ((b & c) | (b & d) | (c & d)) >>> 0;
        k = 0x8f1bbcdc;
      } else {
        f = (b ^ c ^ d) >>> 0;
        k = 0xca62c1d6;
      }
      const temp =
        (((((a << 5) | (a >>> 27)) >>> 0) + f + e + k + (w[i] ?? 0)) >>>
          0);
      e = d;
      d = c;
      c = ((b << 30) | (b >>> 2)) >>> 0;
      b = a;
      a = temp;
    }

    h0 = (h0 + a) >>> 0;
    h1 = (h1 + b) >>> 0;
    h2 = (h2 + c) >>> 0;
    h3 = (h3 + d) >>> 0;
    h4 = (h4 + e) >>> 0;
  }

  const out = new Uint8Array(SHA1_DIGEST_BYTES);
  const writeWord = (word: number, off: number): void => {
    out[off] = (word >>> 24) & 0xff;
    out[off + 1] = (word >>> 16) & 0xff;
    out[off + 2] = (word >>> 8) & 0xff;
    out[off + 3] = word & 0xff;
  };
  writeWord(h0, 0);
  writeWord(h1, 4);
  writeWord(h2, 8);
  writeWord(h3, 12);
  writeWord(h4, 16);

  let hex = '';
  for (let i = 0; i < out.length; i += 1) {
    const byte = out[i] ?? 0;
    hex += ((byte >>> 4) & 0x0f).toString(16) + (byte & 0x0f).toString(16);
  }
  return hex;
}

/**
 * Deterministic JSON serialisation with keys sorted lexicographically.
 *
 * Why: `JSON.stringify({ a: 1, b: 2 })` and `JSON.stringify({ b: 2, a: 1 })`
 * produce byte-different strings, which would hash to different values and
 * break cache equality. Sorting the keys ensures same filter set → same
 * hash, irrespective of property declaration order.
 *
 * Handles `undefined`, `Date` (→ `toISOString`), and circular references
 * gracefully (cycles replaced with the literal string `"[Circular]"`).
 */
export function stableStringify(value: unknown): string {
  const seen = new WeakSet<object>();
  const visit = (node: unknown): string => {
    if (node === null) return 'null';
    const nodeType = typeof node;
    if (nodeType === 'undefined') return '"__undefined__"';
    if (nodeType === 'string') return JSON.stringify(node);
    if (nodeType === 'number') {
      return Number.isFinite(node as number) ? String(node) : 'null';
    }
    if (nodeType === 'boolean') return (node as boolean) ? 'true' : 'false';
    if (nodeType === 'bigint') return `"${(node as bigint).toString()}n"`;
    if (nodeType === 'function') return '"__function__"';

    if (node instanceof Date) return JSON.stringify(node.toISOString());

    if (Array.isArray(node)) {
      if (seen.has(node)) return '"[Circular]"';
      seen.add(node);
      const parts = node.map((entry) => visit(entry));
      seen.delete(node);
      return `[${parts.join(',')}]`;
    }

    if (nodeType === 'object') {
      const obj = node as Record<string, unknown>;
      if (seen.has(obj)) return '"[Circular]"';
      seen.add(obj);
      const keys = Object.keys(obj).sort();
      const parts: string[] = [];
      for (const key of keys) {
        parts.push(`${JSON.stringify(key)}:${visit(obj[key])}`);
      }
      seen.delete(obj);
      return `{${parts.join(',')}}`;
    }

    return `"__${nodeType}__"`;
  };
  return visit(value);
}

/**
 * Compute the cache key for a given filter set.
 *
 * Equivalent to the task packet's `sha1(JSON.stringify(filters))` — but
 * re-exported as a pure function so consumers (e.g. `useWidgetData` for
 * effect-deps tracking) can compute hashes outside the hook.
 */
export function computeFilterHash(filters: unknown): string {
  return sha1Hex(stableStringify(filters));
}

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * Per-filter memo cache for widget / insights data fetches.
 *
 * @example
 *   const insightsCache = useInsightsCache<MyInsightPayload>();
 *   const cached = insightsCache.get(filters);
 *   if (cached && !cached.isStale) return cached.data;
 *   const controller = new AbortController();
 *   insightsCache.beginFetch(filters, controller);
 *   const data = await fetch(...);
 *   insightsCache.set(filters, data);
 *   insightsCache.endFetch(filters);
 */
export function useInsightsCache<T = unknown>(
  options?: UseInsightsCacheOptions,
): UseInsightsCacheApi<T> {
  const ttlMs = options?.ttlMs ?? DEFAULT_INSIGHTS_TTL_MS;

  // AC1: Map<filterHash, InsightsState>. Stored in a ref so changes don't
  // trigger re-renders (consumers opt-in to re-reads via `get`).
  const cacheRef = useRef<Map<string, InsightsState<T>>>(new Map());

  // In-flight AbortController registry keyed by filterHash. AC5 helper:
  // `useWidgetData` calls `beginFetch` before each POST so the cache can
  // abort a losing race via `cancelInFlight(filters)` on the next change.
  const inflightRef = useRef<Map<string, AbortController>>(new Map());

  const get = useCallback(
    (filters: unknown): InsightsCacheHit<T> | null => {
      const hash = computeFilterHash(filters);
      const entry = cacheRef.current.get(hash);
      if (entry === undefined) return null;
      const age = Date.now() - entry.fetchedAt;
      return {
        data: entry.data,
        fetchedAt: entry.fetchedAt,
        isStale: age >= ttlMs,
      };
    },
    [ttlMs],
  );

  const set = useCallback((filters: unknown, data: T): string => {
    const hash = computeFilterHash(filters);
    cacheRef.current.set(hash, { data, fetchedAt: Date.now() });
    return hash;
  }, []);

  const clear = useCallback((): void => {
    for (const [, controller] of inflightRef.current) {
      try {
        controller.abort('cache:cleared');
      } catch {
        // AbortController.abort() doesn't throw — catch guards polyfills.
      }
    }
    inflightRef.current.clear();
    cacheRef.current.clear();
  }, []);

  const beginFetch = useCallback(
    (filters: unknown, controller: AbortController): string => {
      const hash = computeFilterHash(filters);
      // Cancel any prior in-flight for the same hash to prevent two
      // concurrent writers racing for the same slot.
      const existing = inflightRef.current.get(hash);
      if (existing && existing !== controller) {
        try {
          existing.abort('cache:replaced');
        } catch {
          // noop
        }
      }
      inflightRef.current.set(hash, controller);
      return hash;
    },
    [],
  );

  const endFetch = useCallback((filters: unknown): void => {
    const hash = computeFilterHash(filters);
    inflightRef.current.delete(hash);
  }, []);

  const cancelInFlight = useCallback((filters: unknown): void => {
    const hash = computeFilterHash(filters);
    const controller = inflightRef.current.get(hash);
    if (controller) {
      try {
        controller.abort('cache:filter-changed');
      } catch {
        // noop
      }
      inflightRef.current.delete(hash);
    }
  }, []);

  return useMemo(
    () => ({
      get,
      set,
      clear,
      beginFetch,
      endFetch,
      cancelInFlight,
    }),
    [get, set, clear, beginFetch, endFetch, cancelInFlight],
  );
}

// Internal helpers / test surface (exported for unit tests + advanced use).
export const __INTERNAL = {
  SHA1_DIGEST_BYTES,
  DEFAULT_INSIGHTS_TTL_MS,
};
